import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, CheckCircle, HelpCircle, Users, Search, MessageSquare, Award, ArrowLeft, Lightbulb, User, Building, Heart, Coffee } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const FinalInterviewSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {
  
  const finalInterviewTypes = [
    {
      icon: User,
      title: "1. Line manager interview",
      duration: "~40 mins with the person you'd report to",
      focus: "Focuses on working style, self-awareness and day-to-day team fit",
      questions: [
        "What could you have done better in the last round?",
        "How do you like to be managed?"
      ]
    },
    {
      icon: Building,
      title: "2. Founder interview",
      duration: "Often with a co-founder or CEO — especially in smaller startups",
      focus: "Designed to test deep motivation for the company/mission and strategic thinking and self-drive",
      questions: [
        "Why should we hire you over someone else?",
        "What would you do in your first few weeks here?",
        "What's your most impressive achievement?"
      ]
    },
    {
      icon: Heart,
      title: "3. Culture / values interview",
      duration: "Typically with someone from people / ops or a senior team member",
      focus: "Tests alignment with the startup's values and ways of working. You should scour the startup's video pitch, website & JD for any references to values or culture and think of 2-3 examples where you've shown these traits",
      questions: [
        "What motivates you?",
        "What do you look for in a team culture?"
      ]
    },
    {
      icon: Coffee,
      title: "4. Team fit interview",
      duration: "More informal — could be coffee, lunch, or casual Zooms with teammates",
      focus: "The goal is simple: are you easy to get along with?",
      questions: [
        "What do you like doing outside of work?",
        "What does a typical Saturday look like?",
        "What excites you most about joining the team?"
      ]
    }
  ];

  const greatQuestions = [
    "If I joined next week, what would you expect me to take ownership of in month one?",
    "What traits have made people succeed or struggle in this role before?",
    "What's the biggest unknown the company is trying to figure out right now?",
    "If I asked the rest of the team what it's like to work here, what do you think they'd say across their different roles?"
  ];

  const questionsToAvoid = [
    "Overused questions like 'what's the culture like?' or 'where do you see the company in 5 years?'",
    "Anything you can easily find on the website",
    "Questions about salary, hours or flexibility — save those for once an offer (hopefully!) lands"
  ];

  const checklist = [
    "Prepared at least 3 thoughtful, well-researched questions",
    "Considered how to demonstrate a growth mindset — e.g. what you could have done better in the previous rounds",
    "Know your 2-3 top impact stories, with clear, evidence-backed examples of your best work",
    "Look at the startup's video pitch, website & JD for any references to values or culture and think of 2-3 examples where you've shown these traits",
    "Refresh your response to 'why this startup?' — showing you understand the product and the market",
    "Be prepared to bring good energy / vibes to show you're an excellent team fit"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#02B491]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-10 h-10 text-[#02B491]" />
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Final interviews: sealing the deal
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Navigate culture fit and self-awareness questions to close the deal.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={onBack}
              className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold transition-colors ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-lg'
                  : 'bg-[#02B491] text-white hover:bg-[#029078] shadow-md'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-12">
          
          <StepCard title="🏆 What final interviews are really about">
            <div className="space-y-8">
              <div>
                <p className="text-gray-600 mb-6">
                  By this stage, the startup probably already thinks:
                </p>
                <div className="space-y-3 mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></div>
                    <span className="text-gray-700 font-medium">You're smart</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></div>
                    <span className="text-gray-700 font-medium">You can do the job</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-8">
                <p className="text-gray-600 mb-6">Now they're asking:</p>
                <div className="space-y-4">
                  <p className="text-gray-800 italic">"Do I want to work with this person every day?"</p>
                  <p className="text-gray-800 italic">"Will they fit into our team's vibe and way of working?"</p>
                  <p className="text-gray-800 italic">"Are they reflective, self-aware and easy to collaborate with?"</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
              <p className="text-yellow-800 text-sm">
                <strong>Key insight:</strong> Final rounds are more about chemistry than capability. Use this stage to show how you'd work with the team day-to-day.
              </p>
            </div>
          </StepCard>

          <StepCard title="🎭 Types of final interviews you might face">
            <div className="space-y-6">
              {finalInterviewTypes.map((type, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="w-10 h-10 bg-[#02B491]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <type.icon className="w-5 h-5 text-[#02B491]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 mb-2">{type.title}</h4>
                      <p className="text-gray-600 mb-2">{type.duration}</p>
                      <p className="text-gray-700 mb-4">{type.focus}</p>
                      
                      {type.questions.length > 0 && (
                        <div>
                          <h5 className="font-semibold text-gray-900 mb-2">
                            {type.title.includes('Common') ? 'Questions might include:' : 'You might be asked:'}
                          </h5>
                          <ul className="space-y-1">
                            {type.questions.map((question, i) => (
                              <li key={i} className="text-gray-700 italic">• {question}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </StepCard>

          <StepCard title="Asking great questions in final interviews">
            <div className="space-y-8">
              <p className="text-gray-600">
                Asking strong questions is one of the easiest ways to leave a lasting impression.
              </p>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-6">Great questions do one (or more) of the following:</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">Show you've thought about how you'd add value</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">Reveal insight into startup realities (e.g. ambiguity, prioritisation, team dynamics)</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">Demonstrate genuine curiosity and commercial awareness</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700">Reference previous conversations or earlier interview rounds to show that you've been listening and reflecting</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h4 className="font-semibold text-green-900 mb-4">Some strong examples:</h4>
                {greatQuestions.map((question, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 mb-3 border border-green-200">
                    <p className="text-green-800 italic">{question}</p>
                  </div>
                ))}
              </div>

              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h4 className="font-semibold text-red-900 mb-4">Avoid:</h4>
                <ul className="space-y-2">
                  {questionsToAvoid.map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <span className="text-red-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </StepCard>

          <StepCard title="Final interview checklist">
            <p className="text-gray-600 mb-6">
              Before your final interview, make sure you've:
            </p>
            <div className="space-y-3">
              {checklist.map((item, i) => (
                <label key={i} className="flex items-start space-x-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="mt-1 w-5 h-5 text-[#02B491] border-gray-300 rounded focus:ring-[#02B491]" 
                  />
                  <span className="text-gray-700">{item}</span>
                </label>
              ))}
            </div>
          </StepCard>

        </div>
        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinalInterviewSection; 